/**
 * @file keywords.ts
 * @description «Ключевые запросы» в формах кабинета (phase19.16, партия Р3в,
 * v0.19.64; SEO11, SEO12; контракт r3v-api-contract §4): значение поля,
 * добавление чипа (без пустых и повторов с учётом регистра и «ё»), поля
 * ответа сервера и тело запроса. Чистые функции — проверяют тесты.
 */

import { normalizeSeoKeyword } from "@chaiberi/shared";

/** Предел длины запроса (контракт §4). */
export const SEO_KEYWORD_MAX = 100;
/** Предел числа дополнительных запросов. */
export const SEO_KEYWORDS_EXTRA_MAX = 20;

/** Значение поля в форме. */
export interface SeoKeywordsValue {
  main: string;
  extra: string[];
}

/** У кого ещё такой же главный запрос (SEO12). */
export interface SeoKeywordConflict {
  type: "page" | "product" | "category" | "article";
  id: string;
  title: string;
}

/** Поля ответа сервера у страницы, товара, категории, статьи. */
export interface SeoKeywordsDto {
  seoKeywordMain?: string | null;
  seoKeywordsExtra?: string[];
  seoKeywordConflicts?: SeoKeywordConflict[];
}

/** Поля запроса. */
export interface SeoKeywordsInput {
  seoKeywordMain?: string | null;
  seoKeywordsExtra?: string[];
}

export const EMPTY_SEO_KEYWORDS: SeoKeywordsValue = { main: "", extra: [] };

/** Значение формы по ответу сервера (старый ответ без полей — пусто). */
export function keywordsOf(dto: SeoKeywordsDto | null | undefined): SeoKeywordsValue {
  return { main: dto?.seoKeywordMain ?? "", extra: dto?.seoKeywordsExtra ?? [] };
}

/** Добавить чип: пустой, повтор или сверх предела — тот же массив. */
export function addKeyword(list: readonly string[], raw: string): string[] {
  const word = raw.replace(/\s+/gu, " ").trim().slice(0, SEO_KEYWORD_MAX);
  if (!word || list.length >= SEO_KEYWORDS_EXTRA_MAX) return list as string[];
  const key = normalizeSeoKeyword(word);
  if (list.some((w) => normalizeSeoKeyword(w) === key)) return list as string[];
  return [...list, word];
}

/** Тело запроса: пустой главный — `null` (очистить). */
export function keywordsInput(value: SeoKeywordsValue): SeoKeywordsInput {
  return { seoKeywordMain: value.main.trim() || null, seoKeywordsExtra: value.extra };
}

/** Изменились ли запросы относительно ответа сервера. */
export function keywordsChanged(value: SeoKeywordsValue, dto: SeoKeywordsDto | null | undefined): boolean {
  const base = keywordsOf(dto);
  return value.main.trim() !== base.main.trim() || JSON.stringify(value.extra) !== JSON.stringify(base.extra);
}
